import { Component, OnDestroy, OnInit } from '@angular/core';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { forkJoin, Subject } from 'rxjs';
import { takeUntil, tap } from 'rxjs/operators';
import { DashboardService } from '../../services/dashboard.service';
import { GlobalsService } from '../../services/globals.service';
import { ReportsService } from '../../services/reports.service';
import { buildDashboardThemeVars } from '../dashboard-widget-theme';

interface TargetMonth {
	period: string;   // YYYY-MM
	label: string;
	amount: number | null;
	original: number | null;
}

@Component({
	selector: 'app-sales-target-editor',
	templateUrl: './sales-target-editor.component.html',
	styleUrls: ['./sales-target-editor.component.scss'],
})
export class SalesTargetEditorComponent implements OnInit, OnDestroy {
	private static readonly MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

	targetType: 'location' | 'salesman' = 'location';
	targetKey = '';
	year = new Date().getFullYear();

	locations: any[] = [];
	salespeople: any[] = [];
	months: TargetMonth[] = [];
	loading = false;
	saving = false;
	saveError = '';

	private readonly destroy$ = new Subject<void>();

	get themeVars(): Record<string, string> {
		return buildDashboardThemeVars('ste', 'success');
	}

	get entityOptions(): any[] {
		return this.targetType === 'location' ? this.locations : this.salespeople;
	}

	get yearTotal(): number {
		return this.months.reduce((sum, m) => sum + (m.amount || 0), 0);
	}

	get dirtyCount(): number {
		return this.months.filter(m => (m.amount || 0) !== (m.original || 0)).length;
	}

	constructor(
		public activeModal: NgbActiveModal,
		private dashboardService: DashboardService,
		private globalsService: GlobalsService,
		private reportsService: ReportsService,
	) {}

	ngOnInit(): void {
		this.globalsService.getLocations().pipe(takeUntil(this.destroy$)).subscribe((r: any) => {
			this.locations = r || [];
		});
		this.reportsService.getSalesPeople().pipe(takeUntil(this.destroy$)).subscribe((r: any) => {
			this.salespeople = r || [];
		});
		this.buildMonths();
	}

	ngOnDestroy(): void {
		this.destroy$.next();
		this.destroy$.complete();
	}

	onTypeChange(): void {
		this.targetKey = '';
		this.buildMonths();
	}

	onContextChange(): void {
		this.buildMonths();
		if (this.targetKey) {
			this.loadTargets();
		}
	}

	entityValue(option: any): string {
		return this.targetType === 'location' ? option.loccode : option.salesmancode;
	}

	entityLabel(option: any): string {
		return this.targetType === 'location'
			? (option.locationname || option.loccode)
			: (option.salesmanname || option.salesmancode);
	}

	private buildMonths(): void {
		this.months = SalesTargetEditorComponent.MONTHS.map((label, i) => ({
			period: `${this.year}-${String(i + 1).padStart(2, '0')}`,
			label,
			amount: null,
			original: null,
		}));
	}

	private loadTargets(saving: string | boolean = false): void {
		this.loading = true;
		this.dashboardService.getSalesTargets({ type: this.targetType, key: this.targetKey, year: this.year })
			.pipe(takeUntil(this.destroy$))
			.subscribe({
				next: (rows: any) => {
					const byPeriod: Record<string, number> = {};
					(rows || []).forEach((r: any) => { byPeriod[r.period] = parseFloat(r.target_amount) || 0; });
					this.applyLoaded(byPeriod);
					this.loading = false;
					if(saving){
						this.activeModal.close('saved');
					}
				},
				error: () => {
					// Endpoint unavailable — fall back to local values only.
					this.applyLoaded({});
					this.loading = false;
				}
			});
	}

	private applyLoaded(byPeriod: Record<string, number>): void {
		// Local edits overlay the endpoint values until the backend fields exist.
		const local = this.dashboardService.getLocalTargetMap();
		this.months.forEach(m => {
			const localVal = local[`${this.targetType}:${this.targetKey}:${m.period}`];
			m.amount = localVal ?? byPeriod[m.period] ?? null;
			m.original = m.amount;
		});
	}

	copyToAll(month: TargetMonth): void {
		const value = month.amount || 0;
		this.months.forEach(m => { m.amount = value; });
	}

	save(): void {
		if (!this.targetKey || this.saving) { return; }
		const dirty = this.months.filter(m => (m.amount || 0) !== (m.original || 0));
		if (!dirty.length) { this.activeModal.close('saved'); return; }

		// Interim per Sean: persist to localStorage; the savetarget endpoint stays
		// this.dashboardService.saveLocalTarget(this.targetType, this.targetKey, m.period, m.amount || 0);
		const calls = dirty.map(m =>{
			const data = {"target_type": this.targetType, "target_key": this.targetKey, "period": m.period, "target_amount": m.amount || 0}
			return this.dashboardService.saveSalesTarget(data).pipe(
			  tap((res: any) => m.original = m.amount)
			)
		});
		forkJoin(calls).subscribe(() => {
			this.loadTargets('saving');
		});
	}
}